/**
 * Plan-limit upgrade dialog: Office Dialog API wrapper.
 * Shows once per session per feature; supports "Don't show again this session".
 */

/* global Office */

export interface PlanLimitDialogArgs {
  feature: string;
  requestedCount: number;
  insertedCount: number;
  limit: number;
  planName: string;
  upgradeUrl?: string;
  allowDontShowAgainThisSession: boolean;
}

export interface PlanLimitDialogResult {
  action: "upgrade" | "continue";
  dontShowAgain: boolean;
}

const SHOWN_THIS_SESSION_BY_FEATURE: Record<string, boolean> = {};

/**
 * Whether the plan-limit modal has already been shown this session for the given feature.
 */
export function wasPlanLimitDialogShownThisSession(feature: string): boolean {
  return SHOWN_THIS_SESSION_BY_FEATURE[feature] === true;
}

/**
 * Mark that the plan-limit modal was shown (and optionally "don't show again") for this feature this session.
 */
export function setPlanLimitDialogShownThisSession(feature: string): void {
  SHOWN_THIS_SESSION_BY_FEATURE[feature] = true;
}

/**
 * Build dialog URL with query params (same-origin; dialog must be on add-in domain).
 */
function buildPlanLimitDialogUrl(args: PlanLimitDialogArgs): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const params = new URLSearchParams();
  params.set("feature", args.feature);
  params.set("requested", String(args.requestedCount));
  params.set("inserted", String(args.insertedCount));
  params.set("limit", String(args.limit));
  params.set("plan", encodeURIComponent(args.planName));
  params.set("allowDontShowAgain", args.allowDontShowAgainThisSession ? "1" : "0");
  if (args.upgradeUrl) {
    params.set("upgradeUrl", encodeURIComponent(args.upgradeUrl));
  }
  return `${origin}/dialogs/planLimitDialog.html?${params.toString()}`;
}

function parseDialogMessage(raw: string): PlanLimitDialogResult | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const p = parsed as Record<string, unknown>;
    const action = p.action;
    if (action !== "upgrade" && action !== "continue") return null;
    const dontShowAgain = p.dontShowAgain === true;
    return { action, dontShowAgain };
  } catch {
    return null;
  }
}

/**
 * Show the plan-limit reached dialog via Office Dialog API.
 * Resolves when the user clicks Upgrade or Continue; rejects on dialog error or close without response.
 */
export function showPlanLimitDialog(args: PlanLimitDialogArgs): Promise<PlanLimitDialogResult> {
  return new Promise((resolve, reject) => {
    if (typeof Office === "undefined" || !Office.context?.ui?.displayDialogAsync) {
      reject(new Error("Office Dialog API not available"));
      return;
    }
    const url = buildPlanLimitDialogUrl(args);
    Office.context.ui.displayDialogAsync(
      url,
      { height: 45, width: 35, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error("Failed to open plan-limit dialog"));
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          const msg = parseDialogMessage((arg as { message: string }).message);
          if (!msg) return;
          dialog.close();
          resolve(msg);
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          dialog.close();
          resolve({ action: "continue", dontShowAgain: false });
        });
      }
    );
  });
}
